<script>
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  components: {
    Dialog,
    NextButton,
    NextInput,
    NextSelect,
    NextTextArea,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isCreating: false,
      isDeletingTemplate: false,
      isFetching: false,
      isSyncing: false,
      templates: [],
      template: {
        name: '',
        category: 'UTILITY',
        language: 'en',
        headerText: '',
        headerExamples: {},
        body: '',
        bodyExamples: {},
        footer: '',
        buttons: [],
      },
    };
  },
  computed: {
    categoryOptions() {
      return [
        {
          value: 'UTILITY',
          label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.UTILITY'),
        },
        {
          value: 'MARKETING',
          label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORIES.MARKETING'),
        },
      ];
    },
    languageOptions() {
      return [
        { value: 'en', label: 'en' },
        { value: 'pt_BR', label: 'pt_BR' },
      ];
    },
    buttonOptions() {
      return [
        { value: 'QUICK_REPLY', label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.QUICK_REPLY') },
        { value: 'URL', label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.URL') },
        { value: 'PHONE_NUMBER', label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.PHONE_NUMBER') },
        { value: 'COPY_CODE', label: this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.COPY_CODE') },
      ];
    },
    canCreate() {
      return (
        this.template.name &&
        this.template.body &&
        this.headerVariables.every(variable => this.template.headerExamples[variable]) &&
        this.bodyVariables.every(variable => this.template.bodyExamples[variable]) &&
        this.template.buttons.every(button => this.validButton(button)) &&
        !this.isCreating
      );
    },
    headerVariables() {
      return this.extractVariables(this.template.headerText);
    },
    bodyVariables() {
      return this.extractVariables(this.template.body);
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    resetTemplate() {
      this.template = {
        name: '',
        category: 'UTILITY',
        language: 'en',
        headerText: '',
        headerExamples: {},
        body: '',
        bodyExamples: {},
        footer: '',
        buttons: [],
      };
    },
    extractVariables(text = '') {
      const matches = text.match(/\{\{(\d+)\}\}/g) || [];
      return [...new Set(matches.map(match => match.replace(/\D/g, '')))].sort(
        (left, right) => Number(left) - Number(right)
      );
    },
    addHeaderVariable() {
      const lastVariable = Math.max(0, ...this.headerVariables.map(Number));
      const variable = `{{${lastVariable + 1}}}`;
      this.template.headerText = [this.template.headerText, variable]
        .filter(Boolean)
        .join(' ');
    },
    addBodyVariable() {
      const lastVariable = Math.max(0, ...this.bodyVariables.map(Number));
      const variable = `{{${lastVariable + 1}}}`;
      this.template.body = [this.template.body, variable]
        .filter(Boolean)
        .join(' ');
    },
    addButton() {
      this.template.buttons.push({
        type: 'QUICK_REPLY',
        text: '',
        url: '',
        phone_number: '',
        example: '',
      });
    },
    removeButton(index) {
      this.template.buttons.splice(index, 1);
    },
    validButton(button) {
      if (button.type === 'COPY_CODE') return button.example;
      if (button.type === 'URL') return button.text && button.url;
      if (button.type === 'PHONE_NUMBER') return button.text && button.phone_number;
      return button.text;
    },
    templatePayload() {
      return {
        name: this.template.name,
        category: this.template.category,
        language: this.template.language,
        header_format: this.template.headerText ? 'TEXT' : 'NONE',
        header_text: this.template.headerText,
        header_examples: this.headerVariables.map(
          variable => this.template.headerExamples[variable]
        ),
        body: this.template.body,
        body_examples: this.bodyVariables.map(
          variable => this.template.bodyExamples[variable]
        ),
        footer: this.template.footer,
        buttons: this.template.buttons,
      };
    },
    openCreateTemplateModal() {
      this.$refs.createTemplateDialog.open();
    },
    closeCreateTemplateModal() {
      this.resetTemplate();
    },
    bodyText(template) {
      return (
        template.components?.find(component => component.type === 'BODY')
          ?.text || ''
      );
    },
    variableToken(variable) {
      return `{{${variable}}}`;
    },
    async fetchTemplates() {
      this.isFetching = true;
      try {
        const response = await this.$store.dispatch(
          'inboxes/getWhatsAppTemplates',
          this.inbox.id
        );
        this.templates = response.templates || [];
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.ERROR_MESSAGE'));
      } finally {
        this.isFetching = false;
      }
    },
    async createTemplate() {
      this.isCreating = true;
      try {
        const response = await this.$store.dispatch(
          'inboxes/createWhatsAppTemplate',
          {
            inboxId: this.inbox.id,
            template: this.templatePayload(),
          }
        );
        this.templates = [response.template, ...this.templates];
        this.resetTemplate();
        this.$refs.createTemplateDialog.close();
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.ERROR_MESSAGE'));
      } finally {
        this.isCreating = false;
      }
    },
    async deleteTemplate(templateName) {
      if (
        !window.confirm(
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.DELETE_CONFIRM')
        )
      ) {
        return;
      }

      this.isDeletingTemplate = true;
      try {
        await this.$store.dispatch('inboxes/deleteWhatsAppTemplate', {
          inboxId: this.inbox.id,
          templateName,
        });
        this.templates = this.templates.filter(
          template => template.name !== templateName
        );
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.ERROR_MESSAGE'));
      } finally {
        this.isDeletingTemplate = false;
      }
    },
    async syncTemplates() {
      this.isSyncing = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncing = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="flex flex-col gap-4 mb-6 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 class="mb-1 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUBTITLE') }}
        </p>
      </div>
      <div class="flex flex-shrink-0 gap-2">
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.SYNC')"
          :is-loading="isSyncing"
          icon="i-lucide-refresh-cw"
          faded
          @click="syncTemplates"
        />
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.NEW')"
          icon="i-lucide-plus"
          @click="openCreateTemplateModal"
        />
      </div>
    </div>

    <div class="overflow-x-auto border rounded-lg border-n-weak">
      <div
        class="grid grid-cols-12 gap-3 min-w-[48rem] px-4 py-3 text-xs font-medium uppercase text-n-slate-11 border-b border-n-weak"
      >
        <span class="col-span-3">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.NAME') }}
        </span>
        <span class="col-span-2">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.STATUS') }}
        </span>
        <span class="col-span-2">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.CATEGORY') }}
        </span>
        <span class="col-span-2">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.LANGUAGE') }}
        </span>
        <span class="col-span-2">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.BODY') }}
        </span>
        <span class="col-span-1 text-right">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.ACTIONS') }}
        </span>
      </div>
      <div v-if="isFetching" class="px-4 py-6 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.LOADING') }}
      </div>
      <div
        v-else-if="!templates.length"
        class="flex flex-col items-center gap-3 px-4 py-10 text-center"
      >
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.EMPTY') }}
        </p>
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.NEW')"
          icon="i-lucide-plus"
          @click="openCreateTemplateModal"
        />
      </div>
      <template v-else>
        <div
          v-for="item in templates"
          :key="item.id || item.name"
          class="grid grid-cols-12 gap-3 min-w-[48rem] px-4 py-3 text-sm border-b last:border-b-0 border-n-weak text-n-slate-12"
        >
          <span class="col-span-3 truncate">{{ item.name }}</span>
          <span class="col-span-2 truncate">{{ item.status || '-' }}</span>
          <span class="col-span-2 truncate">{{ item.category || '-' }}</span>
          <span class="col-span-2 truncate">{{ item.language || '-' }}</span>
          <span class="col-span-2 truncate text-n-slate-11">
            {{ bodyText(item) || '-' }}
          </span>
          <span class="flex justify-end col-span-1">
            <NextButton
              icon="i-lucide-trash"
              size="sm"
              ruby
              faded
              :disabled="isDeletingTemplate"
              @click="deleteTemplate(item.name)"
            />
          </span>
        </div>
      </template>
    </div>

    <Dialog
      ref="createTemplateDialog"
      width="xl"
      :title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.MODAL.TITLE')"
      :description="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.MODAL.DESCRIPTION')"
      :confirm-button-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.CREATE')"
      :cancel-button-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.CANCEL')"
      :disable-confirm-button="!canCreate"
      :is-loading="isCreating"
      overflow-y-auto
      @confirm="createTemplate"
      @close="closeCreateTemplateModal"
    >
      <div class="flex flex-col gap-5">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.LABEL') }}
          </span>
          <NextInput
            v-model="template.name"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.PLACEHOLDER')
            "
          />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.HELP_TEXT') }}
          </span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.LABEL') }}
          </span>
          <NextSelect v-model="template.category" :options="categoryOptions" />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.LABEL') }}
          </span>
          <NextSelect v-model="template.language" :options="languageOptions" />
        </label>

        <div class="flex flex-col gap-3 pt-2 border-t border-n-weak">
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.LABEL') }}
            </span>
            <NextButton
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.ADD_VARIABLE')"
              icon="i-lucide-plus"
              size="sm"
              faded
              type="button"
              @click="addHeaderVariable"
            />
          </div>
          <NextInput
            v-model="template.headerText"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TEXT_PLACEHOLDER')
            "
          />

          <div v-if="headerVariables.length" class="flex flex-col gap-3">
            <p class="mb-0 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.HELP_TEXT') }}
            </p>
            <label
              v-for="variable in headerVariables"
              :key="`header-${variable}`"
              class="flex flex-col gap-1"
            >
              <span class="text-xs font-medium text-n-slate-11">
                {{ variableToken(variable) }}
              </span>
              <NextInput
                v-model="template.headerExamples[variable]"
                :placeholder="
                  $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.PLACEHOLDER')
                "
              />
            </label>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.LABEL') }}
            </span>
            <NextButton
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.ADD_VARIABLE')"
              icon="i-lucide-plus"
              size="sm"
              faded
              type="button"
              @click="addBodyVariable"
            />
          </div>
          <NextTextArea
            v-model="template.body"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.PLACEHOLDER')
            "
            :max-length="1024"
            auto-height
            resize
          />
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.HELP_TEXT') }}
          </p>
        </div>

        <div v-if="bodyVariables.length" class="flex flex-col gap-3">
          <div>
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.LABEL') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.HELP_TEXT') }}
            </p>
          </div>
          <label
            v-for="variable in bodyVariables"
            :key="variable"
            class="flex flex-col gap-1"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ variableToken(variable) }}
            </span>
            <NextInput
              v-model="template.bodyExamples[variable]"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.PLACEHOLDER')
              "
            />
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.LABEL') }}
          </span>
          <NextInput
            v-model="template.footer"
            :placeholder="
              $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.PLACEHOLDER')
            "
          />
        </label>

        <div class="flex flex-col gap-3 pt-2 border-t border-n-weak">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.LABEL') }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.HELP_TEXT') }}
              </p>
            </div>
            <NextButton
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.ACTIONS.ADD_BUTTON')"
              icon="i-lucide-plus"
              size="sm"
              faded
              type="button"
              @click="addButton"
            />
          </div>

          <div
            v-for="(button, index) in template.buttons"
            :key="index"
            class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak"
          >
            <div class="flex items-center justify-between gap-3">
              <NextSelect v-model="button.type" :options="buttonOptions" />
              <NextButton
                icon="i-lucide-trash"
                size="sm"
                ruby
                faded
                type="button"
                @click="removeButton(index)"
              />
            </div>

            <NextInput
              v-if="button.type !== 'COPY_CODE'"
              v-model="button.text"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TEXT_PLACEHOLDER')
              "
            />
            <NextInput
              v-if="button.type === 'URL'"
              v-model="button.url"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL_PLACEHOLDER')
              "
            />
            <NextInput
              v-if="button.type === 'URL'"
              v-model="button.example"
              :placeholder="
                $t(
                  'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL_EXAMPLE_PLACEHOLDER'
                )
              "
            />
            <NextInput
              v-if="button.type === 'PHONE_NUMBER'"
              v-model="button.phone_number"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.PHONE_PLACEHOLDER')
              "
            />
            <NextInput
              v-if="button.type === 'COPY_CODE'"
              v-model="button.example"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.CODE_PLACEHOLDER')
              "
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
